import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import FormDialog from '../components/ClientCard';
import { fetchData, fetchDataPromise } from '../services/ApiServices';
import { Paper, Grid, Box, Button, Avatar, Chip, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import CircleIcon from '@mui/icons-material/Circle';
import { urlClients } from '../config';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { useAuthStore } from '../store/authStore';
import { startOfMonth, format } from 'date-fns';
import Controls from '../components/controls/Controls';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import * as MyCollections from "../services/MyCollections";
import { useThemeContext } from '../components/contexts/ThemeContext';
import CustomTooltip from '../components/CustomTooltip';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20,
  },
}))

const initialValue = MyCollections.clientCardInitialValues;

const ActionRenderer = (props) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Tooltip title="Otwórz kartę klienta">
        <IconButton
          size="small"
          onClick={() => props.context.handleUpdate(props.data)}
          sx={{
            backgroundColor: props.context.darkTheme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(71, 85, 105, 0.1)',
            color: props.context.darkTheme ? '#cbd5e1' : '#475569', // Strong Slate Gray
            borderRadius: '10px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: props.context.darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(71, 85, 105, 0.2)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const CompanyRenderer = (props) => {
  const { companyWorkingName, cooperationStatus, cooperationEndDate, vatStatus } = props.data;

  const isSuspended = cooperationStatus === "SUSPENDED";
  const todayStr = new Date().toISOString().slice(0, 7);
  const isEnded = cooperationStatus === "ENDED" || (cooperationEndDate && cooperationEndDate.slice(0, 7) < todayStr);

  let color = props.context.darkTheme ? '#f8fafc' : '#0f172a';
  let decoration = null;

  if (isSuspended) {
    color = props.context.darkTheme ? '#94a3b8' : '#64748b'; // dimmed slate
    decoration = (
      <Tooltip title="Zawieszony">
        <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: '18px', flexShrink: 0 }} />
      </Tooltip>
    );
  } else if (isEnded) {
    color = props.context.darkTheme ? '#94a3b8' : '#64748b'; // dimmed slate
    decoration = (
      <Tooltip title="Współpraca zakończona">
        <DoNotDisturbIcon sx={{ color: '#ef4444', fontSize: '18px', flexShrink: 0 }} />
      </Tooltip>
    );
  }

  let vatIcon = null;
  if (!isSuspended && !isEnded) {
    if (vatStatus === "CZYNNY") {
      vatIcon = <Tooltip title="VAT Czynny"><CircleIcon sx={{ color: '#10b981', fontSize: '12px' }} /></Tooltip>;
    } else if (vatStatus === "ZWOLNIONY") {
      vatIcon = <Tooltip title="VAT Zwolniony"><CircleIcon sx={{ color: '#3b82f6', fontSize: '12px' }} /></Tooltip>;
    } else if (vatStatus === "NIEZAREJESTROWANY") {
      vatIcon = <Tooltip title="VAT Niezarejestrowany"><CircleIcon sx={{ color: '#f59e0b', fontSize: '12px' }} /></Tooltip>;
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '8px' }}>
      {vatIcon}
      <span style={{ fontWeight: '700', color: color, opacity: (isSuspended || isEnded) ? 0.8 : 1 }}>
        {companyWorkingName}
      </span>
      {decoration}
    </div>
  );
};

const VatStatusChipRenderer = (props) => {
  let val = props.value;
  let color = '#64748b';
  let bg = props.context.darkTheme ? '#1e293b' : '#f1f5f9';
  let label = val || "Niezarejestrowany";

  switch (val) {
    case "CZYNNY":
      color = props.context.darkTheme ? '#6ee7b7' : '#065f46';
      bg = props.context.darkTheme ? '#022c22' : '#d1fae5';
      break;
    case "ZWOLNIONY":
      color = props.context.darkTheme ? '#93c5fd' : '#1e40af';
      bg = props.context.darkTheme ? '#172554' : '#dbeafe';
      break;
    case "NIEZAREJESTROWANY":
      color = props.context.darkTheme ? '#fcd34d' : '#92400e';
      bg = props.context.darkTheme ? '#451a03' : '#fef3c7';
      label = "NIEZAREJ.";
      break;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: bg,
          color: color,
          fontWeight: 'normal',
          borderRadius: '6px',
          height: '24px',
          fontSize: '11px'
        }}
      />
    </div>
  );
};

const StatusChipRenderer = (props) => {
  const val = props.value || "Aktywny";
  let bg = props.context?.darkTheme ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5';
  let color = props.context?.darkTheme ? '#6ee7b7' : '#065f46';

  if (val === "Zawieszony") {
    bg = props.context?.darkTheme ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7';
    color = props.context?.darkTheme ? '#fcd34d' : '#92400e';
  } else if (val === "Zakończony") {
    bg = props.context?.darkTheme ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2';
    color = props.context?.darkTheme ? '#fca5a5' : '#b91c1c';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Chip
        label={val}
        size="small"
        sx={{
          bgcolor: bg,
          color: color,
          fontWeight: 'bold',
          borderRadius: '6px',
          height: '24px',
          fontSize: '11px'
        }}
      />
    </div>
  );
};

const LegalFormChipRenderer = (props) => {
  let val = props.value;
  if (!val) val = "brak";

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Chip
        label={val}
        size="small"
        sx={{
          bgcolor: props.context.darkTheme ? '#042f2e' : '#ccfbf1',
          color: props.context.darkTheme ? '#5eead4' : '#0f766e',
          fontWeight: 'normal',
          borderRadius: '6px',
          height: '24px',
          fontSize: '11px'
        }}
      />
    </div>
  );
};

const CopyableTextRenderer = (props) => {
  const text = props.value;
  if (!text || text === 'brak') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
        <span style={{ color: props.context.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  let iconColor = props.context.darkTheme ? '#94a3b8' : '#64748b';
  if (props.colorType === 'blue') {
    iconColor = props.context.darkTheme ? '#60a5fa' : '#3b82f6';
  } else if (props.colorType === 'red') {
    iconColor = props.context.darkTheme ? '#fb7185' : '#e11d48';
  } else if (props.colorType === 'purple') {
    iconColor = props.context.darkTheme ? '#c084fc' : '#8b5cf6';
  }

  let icon = <PhoneOutlinedIcon sx={{ fontSize: '14px', mr: 1, color: iconColor }} />;
  if (props.colorType === 'blue') {
    icon = <MailOutlineIcon sx={{ fontSize: '14px', mr: 1, color: iconColor }} />;
  } else if (props.colorType === 'red') {
    icon = <AlternateEmailIcon sx={{ fontSize: '14px', mr: 1, color: iconColor }} />;
  } else if (props.colorType === 'purple') {
    icon = <span style={{ marginRight: '6px', fontSize: '10px', fontWeight: 'bold', color: iconColor }}>NIP</span>;
  }

  const textColor = props.context.darkTheme ? '#e2e8f0' : '#334155';

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'space-between', paddingRight: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <span style={{ color: textColor }}>{text}</span>
      </div>
      <Tooltip title="Kopiuj">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ padding: '2px', color: props.context.darkTheme ? '#64748b' : '#94a3b8', '&:hover': { color: textColor } }}
        >
          <ContentCopyIcon sx={{ fontSize: '14px' }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const AvatarRenderer = (props) => {
  const fullName = props.value;
  if (!fullName || fullName === 'brak' || fullName.trim() === '') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
        <span style={{ color: props.context.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>
      </div>
    );
  }
  const parts = fullName.split(' ');
  const first = parts[0] || '';
  const second = parts.length > 1 ? parts[1] : '';
  const initials = `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '8px', padding: '4px 0' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context.darkTheme ? '#334155' : '#e2e8f0',
          color: props.context.darkTheme ? '#cbd5e1' : '#475569',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ color: props.context.darkTheme ? '#f1f5f9' : '#334155' }}>
        {fullName}
      </span>
    </div>
  );
};

const DateCalendarRenderer = (props) => {
  const val = props.valueFormatted || props.value;
  if (!val || val === 'brak') {
    return <span style={{ color: props.context.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '6px' }}>
      <CalendarTodayIcon sx={{ fontSize: '14px', color: props.context.darkTheme ? '#94a3b8' : '#64748b' }} />
      <span style={{ color: props.context.darkTheme ? '#e2e8f0' : '#334155' }}>{val}</span>
    </div>
  );
};


function ClientsList() {
  const { roles: roles } = useAuthStore();

  const token = useAuthStore().token;
  const contactId = useAuthStore().contactId;
  const userName = useAuthStore().fullName;
  const [gridApi, setGridApi] = useState(null)
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState((JSON.parse(JSON.stringify(initialValue))))
  const [selectedLetter, setSelectedLetter] = useState("Wszyscy klienci");
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const today = new Date();
  const gridRef = useRef();
  const [isSuspended, setIsSuspended] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const isForAccountant = roles?.includes('ACCOUNTANT') || roles?.includes('CHIEF_ACCOUNTANT');
  const isExternalIt = roles?.includes('EXTERNAL_IT');
  const [filteredData, setFilteredData] = useState(tableData);
  const [selectedLegalForm, setSelectedLegalForm] = useState(0);
  const totalSuspended = tableData.reduce((sum, c) => {
    if (c.cooperationStatus === "SUSPENDED") {
      return sum + 1;
    }
    return sum;
  }, 0);
  const totalEnded = tableData.reduce((sum, c) => {
    if (c.cooperationStatus === "ENDED") {
      return sum + 1;
    }
    return sum;
  }, 0);
  const totalActive = tableData.reduce((sum, c) => {
    if (c.cooperationStatus !== "SUSPENDED" && c.cooperationStatus !== "ENDED") {
      return sum + 1;
    }
    return sum;
  }, 0);
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  const mapClients = (item) => {
    return {
      ...item,
    };
  };

  useEffect(() => {
    if (isForAccountant) {
      fetchData(urlClients + `/allForAccountantId?id=${contactId}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
    } else {
      fetchData(urlClients + `/letter?letter=${selectedLetter}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
    }
  }, [])

  useEffect(() => {
    if (selectedLegalForm == 0) {
      setFilteredData(tableData); // Pokaż wszystkie dane, jeśli nie wybrano filtra
    } else {
      const filtered = tableData.filter(row => row.legalFormId === selectedLegalForm);
      setFilteredData(filtered);
    }
  }, [selectedLegalForm, tableData]);

  const handleClickOpen = () => {
    setOpen(true);
    setFormData(JSON.parse(JSON.stringify(initialValue)));
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(JSON.parse(JSON.stringify(initialValue)));
    if (isForAccountant) {
      fetchData(urlClients + `/allForAccountantId?id=${contactId}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
    } else {
      fetchData(urlClients + `/letter?letter=${selectedLetter}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
    }
  };

  var filterParamsMonth = {
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      var dateAsString = cellValue;
      if (dateAsString == null) return -1;
      var dateParts = dateAsString.split('-');
      var cellDate = new Date(
        Number(dateParts[0]),
        Number(dateParts[1]) - 1,
        Number('1')
      );

      // Pobierz tylko miesiąc i rok z daty
      const filterMonthYear = format(startOfMonth(filterLocalDateAtMidnight), 'yyyy-MM');
      const cellMonthYear = format(startOfMonth(cellDate), 'yyyy-MM');

      if (cellMonthYear === filterMonthYear) {
        return 0;
      }
      if (cellMonthYear < filterMonthYear) {
        return -1;
      }
      if (cellMonthYear > filterMonthYear) {
        return 1;
      }
      return 0;
    },
    minValidYear: 2020,
    maxValidYear: today.getFullYear(), // Sprawdź, czy zdefiniowano zmienną today w swoim kodzie
    inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
  };

  var filterParams = {
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      var dateAsString = cellValue;
      if (dateAsString == null) return -1;
      var dateParts = dateAsString.split('-');
      var cellDate = new Date(
        Number(dateParts[0]),
        Number(dateParts[1]) - 1,
        Number(dateParts[2])
      );
      if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
        return 0;
      }
      if (cellDate < filterLocalDateAtMidnight) {
        return -1;
      }
      if (cellDate > filterLocalDateAtMidnight) {
        return 1;
      }
      return 0;
    },
    minValidYear: 2020,
    maxValidYear: today.getFullYear(),
    inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
  };


  // Each Column Definition results in one Column.
  const columnDefs = useMemo(() => [
    {
      headerName: "",
      field: "id",
      cellRenderer: ActionRenderer,
      minWidth: 50,
      maxWidth: 50,
      suppressMenu: true,
      filter: false,
      sortable: false,
      lockPosition: 'left',
      hide: isExternalIt,
    },
    {
      headerName: "Firma",
      field: "companyWorkingName",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 150,
      tooltipValueGetter: (params) => {
        const { cooperationStatus, cooperationEndDate, vatStatus } = params.data || {};
        const isSuspended = cooperationStatus === "SUSPENDED";
        const todayStr = new Date().toISOString().slice(0, 7);
        const isEnded = cooperationStatus === "ENDED" || (cooperationEndDate && cooperationEndDate.slice(0, 7) < todayStr);

        if (isSuspended || isEnded) {
          return "";
        }

        switch (vatStatus) {
          case "CZYNNY": return "Status VAT klienta: Czynny";
          case "ZWOLNIONY": return "Status VAT klienta: Zwolniony";
          case "NIEZAREJESTROWANY": return "Status VAT klienta: Niezarejestrowany";
          default: return "";
        }
      },
      cellRenderer: CompanyRenderer,
    },
    {
      headerName: "Forma Prawna",
      field: "legalForm",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 160,
      cellRenderer: LegalFormChipRenderer,
      hide: !isForAccountant || isExternalIt
    },
    {
      headerName: "NIP",
      field: "nip",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 140,
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'purple' },
    },
    {
      headerName: "Status",
      field: "status",
      lockPosition: 'left',
      minWidth: 130,
      filter: 'agTextColumnFilter',
      cellRenderer: StatusChipRenderer,
      valueGetter: (params) => {
        const status = params.data?.cooperationStatus;
        const todayStr = new Date().toISOString().slice(0, 7);
        const isEnded = status === "ENDED" || (params.data?.cooperationEndDate && params.data.cooperationEndDate.slice(0, 7) < todayStr);
        if (status === "SUSPENDED") return "Zawieszony";
        if (isEnded) return "Zakończony";
        return "Aktywny";
      }
    },
    {
      headerName: "VAT",
      field: "vatStatus",
      cellRenderer: VatStatusChipRenderer,
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      filter: false,
      sortable: true,
      hide: !isForAccountant || isExternalIt
    },
    {
      headerName: "Reprezentant",
      field: "clientContact",
      lockPosition: 'left',
      minWidth: 180,
      cellRenderer: AvatarRenderer,
      valueGetter: (params) => params.data?.clientContact || "brak",
      hide: isExternalIt
    },
    {
      headerName: "Email reprezentanta",
      field: "clientContactEmail",
      lockPosition: 'left',
      minWidth: 200,
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'default' },
      hide: true,
      valueGetter: (params) => params.data?.clientContactEmail || "brak"
    },
    {
      headerName: "Księgowa/y",
      field: "currentAccountant",
      lockPosition: 'left',
      minWidth: 180,
      cellRenderer: AvatarRenderer,
      valueGetter: (params) => params.data?.currentAccountant || "brak",
      hide: isForAccountant || isExternalIt
    },
    {
      headerName: "Poprzednie księgowe",
      field: "previousAccountants",
      lockPosition: 'left',
      minWidth: 180,
      cellStyle: { color: '#919190', textAlign: 'left', },
      valueGetter: (params) => {
        const val = params.data?.previousAccountants;
        if (!val) return "brak";
        const list = val.split(',').map(item => item.trim()).filter(Boolean);
        return list.length > 0 ? list.reverse().join(', ') : "brak";
      },
      hide: isForAccountant || isExternalIt
    },
    {
      headerName: "Superwizor",
      field: "currentSupervisor",
      lockPosition: 'left',
      minWidth: 180,
      cellRenderer: AvatarRenderer,
      valueGetter: (params) => params.data?.currentSupervisor || "brak",
      hide: isExternalIt
    },
    {
      headerName: "Start współpracy",
      field: "cooperationStartDate",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      cellRenderer: DateCalendarRenderer,
      valueFormatter: function (params) {
        if (params.data && params.data.cooperationStartDate) {
          const date = new Date(params.data.cooperationStartDate);
          return date.toISOString().slice(0, 7);
        }
        return "brak";
      },
      hide: isExternalIt
    },
    {
      headerName: "Koniec współpracy",
      field: "cooperationEndDate",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      cellRenderer: DateCalendarRenderer,
      valueFormatter: function (params) {
        if (params.data && params.data.cooperationEndDate) {
          const date = new Date(params.data.cooperationEndDate);
          return date.toISOString().slice(0, 7);
        }
        return "brak";
      },
      hide: isExternalIt
    },
    {
      headerName: "Start zawieszenia",
      field: "suspensionStartDate",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      cellRenderer: DateCalendarRenderer,
      valueFormatter: function (params) {
        if (params.data && params.data.suspensionStartDate) {
          const date = new Date(params.data.suspensionStartDate);
          return date.toISOString().slice(0, 7);
        }
        return "brak";
      },
      hide: isForAccountant || isExternalIt
    },
    {
      headerName: "Koniec zawieszenia",
      field: "suspensionEndDate",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      cellRenderer: DateCalendarRenderer,
      valueFormatter: function (params) {
        if (params.data && params.data.suspensionEndDate) {
          const date = new Date(params.data.suspensionEndDate);
          return date.toISOString().slice(0, 7);
        }
        return "brak";
      },
      hide: isForAccountant || isExternalIt
    },
    {
      headerName: "Data wpisu do KRS",
      field: "dateOfEntryInKRS",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      cellRenderer: DateCalendarRenderer,
      valueFormatter: function (params) {
        if (params.data && params.data.dateOfEntryInKRS) {
          const date = new Date(params.data.dateOfEntryInKRS);
          return date.toISOString().slice(0, 7);
        }
        return "brak";
      },
      hide: isForAccountant || isExternalIt
    },
    {
      headerName: "Pierwszy rok obrachunkowy",
      field: "firstFiscalYear",
      filter: 'agNumberColumnFilter',
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'ag-center-header',
      cellRenderer: DateCalendarRenderer,
      valueGetter: function (params) {
        if (params.data && params.data.firstFiscalYear) {
          const date = new Date(params.data.firstFiscalYear);
          return date.getFullYear();
        }
        return null;
      },
      hide: isExternalIt
    },
  ], [isForAccountant, isExternalIt, filterParamsMonth, today]);


  const onChange = (e) => {
    const { value, id } = e.target
    // console.log(value,id)
    setFormData({ ...formData, [id]: value })

  }

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const onBtnExport = useCallback(() => {
    if (isExternalIt) {
      gridRef.current.api.exportDataAsCsv({
        columnKeys: ["companyWorkingName", "nip", "status"],
        fileName: "lista_klientow.csv",
      });
      return;
    }
    const columnsToExport = ["companyWorkingName", "nip", "clientContact", "clientContactEmail", "currentAccountant", "currentSupervisor", "cooperationStartDate", "cooperationEndDate", "suspensionStartDate", "suspensionEndDate", "dateOfEntryInKRS", "firstFiscalYear"];

    gridRef.current.api.exportDataAsCsv({
      columnKeys: columnsToExport,
    });
  }, [isExternalIt]);

  // setting update row data to form data and opening pop up window
  const handleUpdate = async (oldData) => {
    try {
      const clientData = await fetchDataPromise(urlClients + "/" + oldData.id, null, null, console.error, token);
      setFormData(clientData ? clientData : initialValue);
      setOpen(true);
    } catch (error) {
      console.error('Błąd podczas aktualizacji:', error);
    }
  };

  const handleFormSubmit = () => {
    formData.editedBy = userName;

    const method = formData.id ? "PUT" : "POST";
    const endpoint = formData.id ? `/${formData.id}` : '/';

    const activityType = method === "PUT" ? logActionType.CLIENT_CARD_EDIT : logActionType.ADD_NEW_CLIENT;

    fetch(urlClients + endpoint, {
      method: method,
      body: JSON.stringify(formData),
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 400) {
            throw new Error("Klient o podanym numerze NIP już istnieje w bazie");
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.ok;
      })
      .then(data => {
        logActivity(`${activityType} ${formData.companyWorkingName}, NIP: ${formData.nip}`, userName, token);
        handleClose();
      })
      .catch(error => {
        alert(error.message); // Display the error message to the user
        console.error('Error:', error);
      });
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
    editable: false,
    flex: 1,
    tooltipComponent: CustomTooltip,
  }

  const clearFilters = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;

    if (api) {
      api.setFilterModel(null);

      if (api.applyColumnState) {
        api.applyColumnState({ defaultState: { sort: null } });
      } else if (columnApi && columnApi.applyColumnState) {
        columnApi.applyColumnState({ defaultState: { sort: null } });
      } else if (api.setSortModel) {
        api.setSortModel(null);
      }
    }
  }, []);

  const handleCheckboxChange = (e) => {
    const { name, value } = e.target;

    if (name === "isSuspended") {
      setIsSuspended(value);
      if (isForAccountant) {
        fetchData(urlClients + `/allForAccountantId?id=${contactId}&isSuspended=${value}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
      } else {
        fetchData(urlClients + `/letter?letter=${selectedLetter}&isSuspended=${value}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
      }
    } else if (name === "isEnded") {
      setIsEnded(value);
      if (isForAccountant) {
        fetchData(urlClients + `/allForAccountantId?id=${contactId}&isSuspended=${isSuspended}&isEnded=${value}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
      } else {
        fetchData(urlClients + `/letter?letter=${selectedLetter}&isSuspended=${isSuspended}&isEnded=${value}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
      }
    } else if (name === "isActive") {
      setIsActive(value);
      if (isForAccountant) {
        fetchData(urlClients + `/allForAccountantId?id=${contactId}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${value}`, mapClients, setTableData, console.error, token);
      } else {
        fetchData(urlClients + `/letter?letter=${selectedLetter}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${value}`, mapClients, setTableData, console.error, token);
      }
    }
    else {
      if (isForAccountant) {
        fetchData(urlClients + `/allForAccountantId?id=${contactId}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
      } else {
        fetchData(urlClients + `/letter?letter=${selectedLetter}&isSuspended=${isSuspended}&isEnded=${isEnded}&isActive=${isActive}`, mapClients, setTableData, console.error, token);
      }
    }
  };

  const handleLegalFormChange = (e) => {
    setSelectedLegalForm(e.target.value);
  };

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        <Box sx={{ p: 2, pb: 0 }}>
          {/* Kontener główny filtrów i akcji */}
          <div className="controls-bar controls-bar--between">

            {/* Lewa strona: Filtry (Select + Checkboxy) */}
            <div className="controls-bar controls-bar--left" style={{ gap: '20px' }}>
              {!isExternalIt && (
                <Box minWidth={200}>
                  <Controls.MySelect
                    name="legalFormId"
                    label="Forma prawna"
                    value={selectedLegalForm}
                    onChange={handleLegalFormChange}
                    options={MyCollections.allLegalForms}
                  />
                </Box>
              )}
              <div className="controls-bar" style={{ gap: '12px' }}>
                <Controls.Checkbox
                  name="isActive"
                  value={isActive}
                  onChange={handleCheckboxChange}
                  label="Aktywni"
                />
                <Controls.Checkbox
                  name="isSuspended"
                  value={isSuspended}
                  onChange={handleCheckboxChange}
                  label="Zawieszeni"
                />
                <Controls.Checkbox
                  name="isEnded"
                  value={isEnded}
                  onChange={handleCheckboxChange}
                  label="Zakończeni"
                />
              </div>
            </div>

            {/* Prawa strona: Przyciski Akcji (Wersja Premium) */}
            <div className="controls-bar" style={{ gap: '12px' }}>
              {(roles?.includes('ADMIN') ||
                roles?.includes('ADMIN_SUPERVISOR') ||
                roles?.includes('MANAGER') ||
                isExternalIt) && (
                  <Controls.PremiumButton
                    variant="glass"
                    label="CSV"
                    onClick={onBtnExport}
                    icon="fas fa-download"
                  />
                )}

              <Controls.PremiumButton
                variant="glass"
                label="Wyczyść"
                onClick={clearFilters}
                icon="fas fa-filter"
              />

              {!isExternalIt && (roles?.includes('ADMIN') ||
                roles?.includes('ADMIN_SUPERVISOR') ||
                roles?.includes('ACCOUNT_MANAGER') ||
                roles?.includes('MANAGER') ||
                roles?.includes('TRADER')) && (
                  <Controls.PremiumButton
                    variant="blue"
                    label="Dodaj Klienta"
                    onClick={() => handleClickOpen()}
                    icon="fas fa-plus"
                  />
                )}
            </div>
          </div>

          {/* Dolna sekcja: Statystyki w formie badge'y */}
          <div className="stat-badges-row" style={{ marginTop: '16px', marginBottom: '0' }}>
            <div className="stat-badge stat-badge--green">
              <i className="fas fa-user-check"></i>
              Aktywni klienci: <strong>{totalActive}</strong>
            </div>
            <div className="stat-badge stat-badge--amber">
              <i className="fas fa-user-clock"></i>
              Zawieszeni klienci: <strong>{totalSuspended}</strong>
            </div>
            <div className="stat-badge stat-badge--slate">
              <i className="fas fa-user-minus"></i>
              Zakończona współpraca: <strong>{totalEnded}</strong>
            </div>
          </div>
        </Box>

        <Box sx={{ m: '12px' }} />
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }} sx={{ px: 2, pb: 2 }}>
          <Grid item xs={12}>
            <div className="App" >
              <div className="tableData">
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    key={darkTheme ? 'grid-dark' : 'grid-light'}
                    ref={gridRef}
                    rowData={filteredData}
                    context={{ handleUpdate, darkTheme }}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    pagination={true}
                    paginationPageSize={27}
                    suppressBrowserResizeObserver={true}
                    suppressSizeToFit={true}
                    animateRows={true}
                    localeText={MyCollections.localeTextPL}
                    rowSelection='single'
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    headerHeight={45}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div >
                <FormDialog open={open} handleClose={() => handleClose()}
                  onChange={onChange} handleFormSubmit={handleFormSubmit} setFormData={setFormData} formData={formData} />
              </div >
            </div >
          </Grid >
        </Grid >
      </Paper>


    </>
  );
}

export default ClientsList;